import { z } from "zod";
import { authResultSchema, userLocaleSchema } from "./auth.js";
import { defineEndpoint } from "./endpoint.js";
/** Signup, trial, onboarding and legal acceptance (docs/specs/signup.md, SA-01 … SA-04). */
export const BUSINESS_TYPES = ["online", "offline", "both"];
export const businessTypeSchema = z.enum(BUSINESS_TYPES);
const phoneInput = z.string().trim().min(10).max(32);
const otpCode = z.string().regex(/^\d{6}$/);
const name = z.string().trim().min(1).max(200);
/**
 * B-60b: what the signup code proves, chosen by the Super Admin (docs/specs/signup.md Part 2): the phone (SMS) or the
 * e-mail. With `email` the phone is still asked for, but not verified.
 */
export const SIGNUP_VERIFICATIONS = ["sms", "email"];
export const signupVerificationSchema = z.enum(SIGNUP_VERIFICATIONS);
/** The fields of the code request; exactly one of `phone` / `email`, the one the platform's setting asks for. */
export const signupOtpFieldsSchema = z.object({
    phone: phoneInput.optional(),
    email: z.email().max(254).optional(),
    locale: userLocaleSchema.default("en"),
});
/** `POST /v1/signup/otp`: always 202, whether or not the phone or e-mail has an account. */
export const signupOtpRequestSchema = signupOtpFieldsSchema.refine((value) => (value.phone === undefined) !== (value.email === undefined), { message: "Send either phone or email", path: ["phone"] });
/**
 * `POST /v1/signup`: everything SA-01 asks, the code (sent to the phone or the e-mail, by the platform's setting), and
 * the legal documents the person accepted.
 */
export const signupRequestSchema = z.object({
    businessName: name,
    businessType: businessTypeSchema,
    plannedBranches: z.number().int().min(1).max(500),
    ownerName: name,
    email: z.email().max(254),
    phone: phoneInput,
    password: z.string().min(8).max(128),
    code: otpCode,
    locale: userLocaleSchema.default("en"),
    /** IDs of the current Terms and Privacy versions (`GET /v1/legal-documents/current`). */
    acceptedDocumentIds: z.array(z.uuid()).min(1).max(10),
});
// Legal documents ----------------------------------------------------------------------------------------------
export const LEGAL_KINDS = ["terms", "privacy"];
export const legalKindSchema = z.enum(LEGAL_KINDS);
export const legalDocumentSchema = z.object({
    id: z.uuid(),
    kind: legalKindSchema,
    version: z.number().int(),
    titleEn: z.string(),
    titleBn: z.string(),
    urlEn: z.string(),
    urlBn: z.string(),
    publishedAt: z.iso.datetime(),
});
export const legalDocumentListSchema = z.object({ data: z.array(legalDocumentSchema) });
export const legalAcceptanceRequestSchema = z.object({ documentIds: z.array(z.uuid()).min(1).max(10) });
// Onboarding ---------------------------------------------------------------------------------------------------
export const ONBOARDING_STEPS = [
    "business_profile",
    "products",
    "courier",
    "pos_device",
    "first_sale",
];
export const onboardingStepSchema = z.enum(ONBOARDING_STEPS);
export const onboardingSchema = z.object({
    steps: z.array(z.object({ key: onboardingStepSchema, status: z.enum(["done", "skipped", "todo"]) })),
    completed: z.number().int(),
    total: z.number().int(),
    /** The owner closed the widget, or every step is done or skipped. */
    dismissed: z.boolean(),
    /** When the trial ends (null when not on trial). */
    trialEndsAt: z.iso.datetime().nullable(),
});
export const onboardingStepParamsSchema = z.object({ step: onboardingStepSchema });
export const sampleProductsResultSchema = z.object({
    created: z.number().int(),
    /** True when the samples were already there: nothing new was made. */
    alreadyLoaded: z.boolean(),
});
// Endpoints ----------------------------------------------------------------------------------------------------
const signupTag = { tag: "signup" };
export const requestSignupOtp = defineEndpoint({
    ...signupTag,
    name: "requestSignupOtp",
    method: "POST",
    path: "/v1/signup/otp",
    access: { public: true },
    summary: "Send the signup code by SMS or e-mail, as the platform is set (202 for every phone or e-mail)",
    body: signupOtpRequestSchema,
    status: 202,
    errors: ["RATE_LIMITED", "SERVICE_UNAVAILABLE", "SIGNUP_DISABLED"],
});
export const signUp = defineEndpoint({
    ...signupTag,
    name: "signUp",
    method: "POST",
    path: "/v1/signup",
    access: { public: true },
    summary: "Create the business, its owner, first branch and trial in one step, and sign in",
    body: signupRequestSchema,
    status: 201,
    response: authResultSchema,
    errors: [
        "INVALID_OTP",
        "PHONE_REGISTERED",
        "EMAIL_REGISTERED",
        "LEGAL_OUTDATED",
        "RATE_LIMITED",
        "SIGNUP_DISABLED",
    ],
});
export const getCurrentLegalDocuments = defineEndpoint({
    ...signupTag,
    name: "getCurrentLegalDocuments",
    method: "GET",
    path: "/v1/legal-documents/current",
    access: { public: true },
    summary: "The current Terms and Privacy Policy versions",
    status: 200,
    response: legalDocumentListSchema,
});
export const acceptLegalDocuments = defineEndpoint({
    ...signupTag,
    name: "acceptLegalDocuments",
    method: "POST",
    path: "/v1/auth/legal-acceptances",
    access: { signedIn: true },
    summary: "Accept the current Terms / Privacy versions",
    body: legalAcceptanceRequestSchema,
    status: 204,
    errors: ["LEGAL_OUTDATED"],
});
export const getOnboarding = defineEndpoint({
    ...signupTag,
    name: "getOnboarding",
    method: "GET",
    path: "/v1/onboarding",
    access: { permission: "dashboard.view" },
    summary: "The first-steps checklist and its progress",
    status: 200,
    response: onboardingSchema,
});
export const skipOnboardingStep = defineEndpoint({
    ...signupTag,
    name: "skipOnboardingStep",
    method: "POST",
    path: "/v1/onboarding/steps/{step}/skip",
    access: { permission: "settings.manage" },
    summary: "Skip one checklist step",
    params: onboardingStepParamsSchema,
    status: 200,
    response: onboardingSchema,
});
export const dismissOnboarding = defineEndpoint({
    ...signupTag,
    name: "dismissOnboarding",
    method: "POST",
    path: "/v1/onboarding/dismiss",
    access: { permission: "settings.manage" },
    summary: "Hide the checklist",
    status: 200,
    response: onboardingSchema,
});
export const loadSampleProducts = defineEndpoint({
    tag: "catalog",
    name: "loadSampleProducts",
    method: "POST",
    path: "/v1/sample-products",
    access: { permission: "catalog.manage" },
    summary: "Add a few sample products to try the system (once per business)",
    idempotent: true,
    status: 201,
    response: sampleProductsResultSchema,
    errors: ["PLAN_LIMIT_REACHED"],
    entitlement: "products.max",
});
// Events -------------------------------------------------------------------------------------------------------
export const tenantCreatedEventSchema = z.object({
    tenantId: z.uuid(),
    planKey: z.string(),
    businessType: businessTypeSchema,
});
//# sourceMappingURL=signup.js.map